import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Modal, Descriptions, Spin, Tabs, message, Collapse, Tooltip, Typography, App } from 'antd';
import { ExclamationCircleOutlined, DeleteOutlined, EyeOutlined, InfoCircleOutlined, UndoOutlined } from '@ant-design/icons';
import * as API from '../services/api';
import { formatReadableProjectId } from '../utils/helpers';
import { confirmCritical, confirmAction } from '../utils/notifications';
import { useTheme } from '../context/ThemeContext';
import { useThemeStyles } from '../context/ThemeContext';
import { pageShellStyle } from '../theme';

const { Panel } = Collapse;
const { Text } = Typography;

const CollectionManager = () => {
  const { modal, message: messageApi } = App.useApp();
  const { theme } = useTheme();
  const themeStyles = useThemeStyles();
  const [loading, setLoading] = useState(false);
  const [pendingCollections, setPendingCollections] = useState({ deleted: [], edited: [] });
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [collectionDetails, setCollectionDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('deleted');

  useEffect(() => {
    fetchPendingCollections();
  }, []);

  const fetchPendingCollections = async () => {
    setLoading(true);
    try {
      const response = await API.getPendingCollections('admin');
      setPendingCollections(response.data);
    } catch (error) {
      console.error('Error fetching pending collections:', error);
      messageApi.error('Failed to load pending collections');
    } finally {
      setLoading(false);
    }
  };

  const handleViewCollection = async (collection, type) => {
    setSelectedCollection({ ...collection, type });
    setViewModalVisible(true);
    setLoadingDetails(true);
    setCollectionDetails(null);

    try {
      const response = await API.getCollectionDetails('admin', 
        type === 'deleted' ? collection.collection_name : collection.old_collection_name,
        type
      );
      setCollectionDetails(response.data);
    } catch (error) {
      console.error('Error fetching collection details:', error);
      messageApi.error('Failed to load collection details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleConfirmDelete = (collection, type) => {
    const collectionName = type === 'deleted' ? collection.collection_name : collection.old_collection_name;
    
    confirmCritical({
      title: 'Permanently delete collection?',
      content: (
        <div>
          <p>Collection: <strong>{collectionName}</strong></p>
          <p>This will permanently delete:</p>
          <ul style={{ paddingLeft: 20 }}>
            <li>The collection from the vector database</li>
            <li>The project directory and all files</li>
            <li>All markdown files associated with the project</li>
          </ul>
        </div>
      ),
      okText: 'Delete permanently',
      onOk: async () => {
        try {
          const response = await API.confirmDeleteCollection('admin', collectionName, type);
          if (response.data.success) {
            messageApi.success(response.data.message || 'Collection and project deleted successfully');
            fetchPendingCollections();
            if (viewModalVisible) {
              setViewModalVisible(false);
            }
          } else {
            messageApi.warning(response.data.message || 'Deletion completed with warnings');
            fetchPendingCollections();
          }
        } catch (error) {
          console.error('Error deleting collection:', error);
          messageApi.error(error.response?.data?.detail || 'Failed to delete collection');
        }
      },
    });
  };

  const handleRestore = (collection, type) => {
    const collectionName = type === 'deleted' ? collection.collection_name : collection.old_collection_name;
    
    confirmAction({
      title: `Restore project "${collection.project_name}"?`,
      content: (
        <div>
          <p>This will:</p>
          <ul style={{ paddingLeft: 20 }}>
            <li>Remove the deleted flag from the project</li>
            <li>Make the project visible again on the dashboard</li>
            <li>Remove it from the pending deletions list</li>
          </ul>
        </div>
      ),
      okText: 'Restore project',
      onOk: async () => {
        try {
          const response = await API.restoreProject('admin', collectionName, type);
          if (response.data.success) {
            messageApi.success(response.data.message || 'Project restored successfully');
            fetchPendingCollections();
            if (viewModalVisible) {
              setViewModalVisible(false);
            }
          } else {
            messageApi.error(response.data.message || 'Failed to restore project');
          }
        } catch (error) {
          console.error('Error restoring project:', error);
          messageApi.error(error.response?.data?.detail || 'Failed to restore project');
        }
      },
    });
  };

  const deletedColumns = [
    {
      title: 'Collection Name',
      dataIndex: 'collection_name',
      key: 'collection_name',
    },
    {
      title: 'Project Name',
      dataIndex: 'project_name',
      key: 'project_name',
    },
    {
      title: 'Vector Store',
      dataIndex: 'vector_store_type',
      key: 'vector_store_type',
      render: (type) => <Tag color={type === 'milvus' ? 'blue' : 'green'}>{type}</Tag>,
    },
    {
      title: 'Added At',
      dataIndex: 'added_at',
      key: 'added_at',
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: 'Added By',
      dataIndex: 'added_by',
      key: 'added_by',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            icon={<UndoOutlined />}
            onClick={() => handleRestore(record, 'deleted')}
            style={{
              background: 'linear-gradient(135deg, #F62E97 0%, #153CB4 100%)',
              border: 'none',
              color: '#fff',
              borderRadius: '6px'
            }}
          >
            Restore
          </Button>
          <div
            style={{
              background: 'linear-gradient(135deg, #F62E97 0%, #153CB4 100%)',
              padding: '1px',
              borderRadius: '6px',
              display: 'inline-block'
            }}
          >
            <Button
              icon={<EyeOutlined style={{ color: '#F62E97' }} />}
              onClick={() => handleViewCollection(record, 'deleted')}
              style={{
                background: '#15112A',
                border: 'none',
                borderRadius: '5px',
                height: '100%',
                color: '#F62E97'
              }}
            >
              View
            </Button>
          </div>
          <Button
            icon={<DeleteOutlined />}
            onClick={() => handleConfirmDelete(record, 'deleted')}
            style={{
              background: 'transparent',
              border: '1px solid #ff4d4f',
              borderRadius: '6px',
              color: '#ff4d4f'
            }}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  const editedColumns = [
    {
      title: 'Old Collection Name',
      dataIndex: 'old_collection_name',
      key: 'old_collection_name',
    },
    {
      title: 'New Collection Name',
      dataIndex: 'new_collection_name',
      key: 'new_collection_name',
    },
    {
      title: 'Project Name',
      dataIndex: 'project_name',
      key: 'project_name',
    },
    {
      title: 'Vector Store',
      dataIndex: 'vector_store_type',
      key: 'vector_store_type',
      render: (type) => <Tag color={type === 'milvus' ? 'blue' : 'green'}>{type}</Tag>,
    },
    {
      title: 'Added At',
      dataIndex: 'added_at',
      key: 'added_at',
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <div
            style={{
              background: 'linear-gradient(135deg, #F62E97 0%, #153CB4 100%)',
              padding: '1px',
              borderRadius: '6px',
              display: 'inline-block'
            }}
          >
            <Button
              icon={<EyeOutlined style={{ color: '#F62E97' }} />}
              onClick={() => handleViewCollection(record, 'edited')}
              style={{
                background: '#15112A',
                border: 'none',
                borderRadius: '5px',
                height: '100%',
                color: '#F62E97'
              }}
            >
              View
            </Button>
          </div>
          <Button
            icon={<DeleteOutlined />}
            onClick={() => handleConfirmDelete(record, 'edited')}
            style={{
              background: 'transparent',
              border: '1px solid #ff4d4f',
              borderRadius: '6px',
              color: '#ff4d4f'
            }}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div style={pageShellStyle}>
      <Card
        title="Collection Manager"
        style={{
          background: 'linear-gradient(135deg, rgba(148, 22, 127, 0.1) 0%, rgba(21, 60, 180, 0.1) 100%)',
          border: '1px solid #94167F',
        }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'deleted',
              label: `Deleted Collections (${pendingCollections.deleted.length})`,
              children: (
                <Table
                  columns={deletedColumns}
                  dataSource={pendingCollections.deleted}
                  rowKey="collection_name"
                  loading={loading}
                  pagination={{ pageSize: 10, hideOnSinglePage: true }}
                  tableLayout="fixed"
                  scroll={{ x: 'max-content' }}
                />
              ),
            },
            {
              key: 'edited',
              label: `Edited Collections (${pendingCollections.edited.length})`,
              children: (
                <Table
                  columns={editedColumns}
                  dataSource={pendingCollections.edited}
                  rowKey={(record) => `${record.old_collection_name}_${record.added_at}`}
                  loading={loading}
                  pagination={{ pageSize: 10, hideOnSinglePage: true }}
                  tableLayout="fixed"
                  scroll={{ x: 'max-content' }}
                />
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={`Collection Details: ${selectedCollection?.collection_name || selectedCollection?.old_collection_name}`}
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="restore"
            type="primary"
            icon={<UndoOutlined />}
            onClick={() => handleRestore(selectedCollection, selectedCollection?.type)}
          >
            Restore Project
          </Button>,
          <Button
            key="delete"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleConfirmDelete(selectedCollection, selectedCollection?.type)}
          >
            Confirm Delete Permanently
          </Button>,
        ]}
        width={1400}
      >
        {loadingDetails ? (
          <Spin />
        ) : collectionDetails ? (
          <div>
            {/* Collection Information Section */}
            <Collapse defaultActiveKey={['info']} style={{ marginBottom: '24px' }}>
              <Panel header="Collection Information" key="info">
                <Descriptions column={1} bordered>
                  <Descriptions.Item label="Collection Name">
                    {selectedCollection?.type === 'deleted' 
                      ? collectionDetails.collection_name 
                      : collectionDetails.old_collection_name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Vector Store Type">
                    <Tag color={collectionDetails.vector_store_type === 'milvus' ? 'blue' : 'green'}>
                      {collectionDetails.vector_store_type}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Records">
                    {collectionDetails.collection_data?.total_count || 0}
                  </Descriptions.Item>
                  <Descriptions.Item label="Project Name">
                    {collectionDetails.project_name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Project ID">
                    <span title={collectionDetails.project_id}>
                      {formatReadableProjectId(collectionDetails.project_id)}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Added At">
                    {new Date(collectionDetails.added_at).toLocaleString()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Added By">
                    {collectionDetails.added_by}
                  </Descriptions.Item>
                </Descriptions>
              </Panel>
            </Collapse>

            {/* Collection Records Section */}
            {collectionDetails.collection_data?.records && collectionDetails.collection_data.records.length > 0 ? (
              <div>
                <h4 style={{ marginBottom: '16px' }}>
                  Collection Records ({collectionDetails.collection_data.records.length} of {collectionDetails.collection_data.total_count} total)
                </h4>
                <Table
                  tableLayout="fixed"
                  columns={[
                    { 
                      title: 'ID', 
                      dataIndex: 'id', 
                      key: 'id', 
                      width: 200,
                      fixed: 'left'
                    },
                    {
                      title: 'Text Content',
                      dataIndex: 'text',
                      key: 'text',
                      width: 300,
                      ellipsis: {
                        showTitle: false,
                      },
                      render: (text) => (
                        <Tooltip 
                          placement="topLeft" 
                          title={text}
                          overlayStyle={{ maxWidth: '600px' }}
                          overlayInnerStyle={{ 
                            maxWidth: '600px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}
                        >
                          <div style={{ 
                            maxWidth: '300px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {text || 'N/A'}
                          </div>
                        </Tooltip>
                      ),
                    },
                    {
                      title: 'Metadata',
                      dataIndex: 'metadata',
                      key: 'metadata',
                      width: 250,
                      render: (metadata, record) => {
                        if (!metadata || Object.keys(metadata).length === 0) {
                          return <Text type="secondary">No metadata</Text>;
                        }
                        return (
                          <Collapse size="small" style={{ background: 'transparent' }}>
                            <Panel 
                              header={
                                <span style={{ fontSize: '12px' }}>
                                  {Object.keys(metadata).length} field(s) - Click to expand
                                </span>
                              } 
                              key="metadata"
                            >
                              <div style={{ 
                                background: 'rgba(0, 0, 0, 0.3)', 
                                padding: '8px', 
                                borderRadius: '4px',
                                maxHeight: '200px',
                                overflow: 'auto'
                              }}>
                                {Object.entries(metadata).map(([key, value]) => (
                                  <div key={key} style={{ marginBottom: '8px' }}>
                                    <Text strong style={{ color: '#94167F' }}>{key}:</Text>{' '}
                                    <Text>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</Text>
                                  </div>
                                ))}
                              </div>
                            </Panel>
                          </Collapse>
                        );
                      },
                    },
                  ]}
                  dataSource={collectionDetails.collection_data.records}
                  rowKey="id"
                  pagination={{ 
                    pageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    hideOnSinglePage: true,
                  }}
                  scroll={{ x: 1200, y: 500 }}
                  size="small"
                />
              </div>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px',
                color: themeStyles.textSecondary
              }}>
                <InfoCircleOutlined style={{ fontSize: '48px', marginBottom: '16px', color: themeStyles.textSecondary }} />
                <div>No records found in this collection</div>
                <div style={{ fontSize: '12px', marginTop: '8px' }}>
                  Total count: {collectionDetails.collection_data?.total_count || 0}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px',
            color: themeStyles.textSecondary
          }}>
            {collectionDetails?.error ? (
              <>
                <div style={{ color: '#ff4d4f', marginBottom: '8px' }}>Error loading collection data</div>
                <div style={{ fontSize: '12px' }}>{collectionDetails.error}</div>
              </>
            ) : (
              'No collection data available'
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CollectionManager;

